<template>
    <div class="container" style="margin-bottom: 20px;">
      <h2 class="blog-section-title fw-bold mb-4 text-center">Latest Blog Posts</h2>
      
      <!-- Loading State -->
      <div v-if="blogStore.loading" class="text-center">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2">Loading latest blog posts...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="blogStore.error" class="alert alert-warning" role="alert">
        <h4 class="alert-heading">Unable to load blogs</h4>
        <p>{{ blogStore.error }}</p>
        <button @click="loadBlogs" class="btn btn-primary">Try Again</button>
      </div>

      <!-- Blog Content -->
      <div v-else-if="latestBlogs.length > 0">
        <!-- First Row - Featured/Latest 3 -->
        <div class="row mb-4">
          <div v-for="(blog, idx) in latestBlogs.slice(0, 3)" :key="'featured-' + blog.id" class="col-lg-4 col-md-6 mb-4">
            <BlogCard :blog="blog" />
          </div>
        </div>

        <!-- Second Row - Additional 3 -->
        <div v-if="latestBlogs.length > 3" class="row mb-4">
          <div v-for="(blog, idx) in latestBlogs.slice(3, 6)" :key="'additional-' + blog.id" class="col-lg-4 col-md-6 mb-4">
            <BlogCard :blog="blog" />
          </div>
        </div>

        <!-- View All Button -->
        <div class="text-center mt-4">
          <router-link to="/blogs" class="orange-btn">
            View All Blogs <span class="arrow">→</span>
          </router-link>
        </div>
      </div>

      <!-- No Blogs State -->
      <div v-else class="text-center py-5">
        <h4>No blog posts available</h4>
        <p>Check back later for the latest updates.</p>
      </div>
    </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useBlogStore } from '../stores/blogStore'
import BlogCard from './BlogCard.vue'

const blogStore = useBlogStore()

// Get the latest blogs, sorted by creation date
const latestBlogs = computed(() => {
  if (!blogStore.blogs.length) return []
  
  // Sort blogs by creation date (newest first) and take the latest 6
  return blogStore.blogs
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 6)
})

const loadBlogs = async () => {
  await blogStore.fetchBlogs()
}

onMounted(() => {
  // Always try to load blogs if not already loaded
  if (blogStore.blogs.length === 0) {
    loadBlogs()
  }
})
</script>

<style scoped>
.blog-section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 16px;
  letter-spacing: -0.5px;
  text-shadow: 0 2px 4px rgba(0,0,0,0.08);
  padding-bottom: 30px;
}

.blog-card {
  border-radius: 1rem;
  box-shadow: 0 2px 16px rgba(44, 62, 80, 0.07);
  border: none;
  transition: box-shadow 0.2s;
}
.blog-card:hover {
  box-shadow: 0 4px 24px rgba(44, 62, 80, 0.15);
}
.card-img-top {
  border-top-left-radius: 1rem;
  border-top-right-radius: 1rem;
  height: 180px;
  object-fit: cover;
}
.theme-btn {
  background: #3b4fff;
  border: none;
  font-weight: 600;
  padding: 0.75em 2.5em;
  border-radius: 2em;
  box-shadow: 0 2px 8px rgba(44, 62, 80, 0.08);
  transition: background 0.2s;
}
.theme-btn:hover {
  background: #2536b6;
  color: #fff;
}

.blog-card-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 12px 24px;
  font-weight: 500;
  font-size: 16px;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.2);
}

.blog-card-btn:hover {
  background: #1e40af;
  color: #fff;
  text-decoration: none;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
}

.blog-card-btn .arrow {
  margin-left: 8px;
  font-size: 1.1em;
  transition: transform 0.3s ease;
}

.blog-card-btn:hover .arrow {
  transform: translateX(4px);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .blog-section-title {
    font-size: 2rem;
  }
  
  .blog-card-btn {
    padding: 10px 20px;
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .blog-section-title {
    font-size: 1.8rem;
  }
}
</style> 